import { useState, type ReactNode } from 'react';
import { t, type MessageKey } from '../../i18n';
import { Modal } from './Modal';
import { PawnFigure } from './PawnFigure';

const pawn = (side: 'S' | 'N', extra: { marked?: boolean; tag?: string } = {}) => (
  <span className="legend-pawn guide-pawn">
    <PawnFigure
      side={side}
      crowned={false}
      immune={false}
      inscription={null}
      marked={extra.marked}
    />
    {extra.tag && <span className="pawn-name">{extra.tag}</span>}
  </span>
);

/** One picture per step of the guide. */
const PICTURES: readonly ReactNode[] = [
  <span className="guide-tables" key="tables">
    {[1, 2, 3].map((n) => (
      <span key={n} className="guide-table">
        {n}
      </span>
    ))}
  </span>,
  // Head starts are written in his ink, never stacked as chips (a pawnshop, not a casino).
  <span className="guide-antes" key="antes">
    {[0, 20, 50].map((n) => (
      <span key={n} className="guide-ante">
        +{n}
      </span>
    ))}
  </span>,
  <span className="guide-row" key="stable">
    {pawn('S', { tag: 'Ada' })}
    {pawn('S', { tag: 'Bram' })}
    {pawn('S', { tag: 'Cleo' })}
  </span>,
  <span className="guide-row" key="ticket">
    {pawn('S', { marked: true, tag: 'Ada' })}
  </span>,
  <span className="guide-row" key="lost">
    {pawn('S', { marked: true, tag: 'Ada' })}
    <span className="guide-arrow">→</span>
    {pawn('N', { marked: true })}
  </span>,
  <span className="guide-row" key="won">
    {pawn('S', { tag: 'Ada' })}
    <span className="guide-plus">+</span>
    {pawn('S', { tag: 'Dov' })}
  </span>,
  <span className="guide-row" key="former">
    {pawn('N', { tag: 'Cleo' })}
  </span>,
];

/** A short, step-by-step explanation of the Broker's tables (spec §15). */
export function CampaignGuide({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(0);
  const last = PICTURES.length - 1;
  const n = step + 1;
  return (
    <Modal
      title={t('campaignGuide.title')}
      onClose={onClose}
      wide
      className="campaign-guide"
      footer={
        <>
          <span className="guide-progress dim">
            {t('campaignGuide.step', { n, total: PICTURES.length })}
          </span>
          {step > 0 && (
            <button type="button" className="btn" onClick={() => setStep(step - 1)}>
              {t('campaignGuide.back')}
            </button>
          )}
          {step < last ? (
            <button
              type="button"
              className="btn btn-primary"
              data-autofocus
              onClick={() => setStep(step + 1)}
            >
              {t('campaignGuide.next')}
            </button>
          ) : (
            <button type="button" className="btn btn-primary" data-autofocus onClick={onClose}>
              {t('campaignGuide.done')}
            </button>
          )}
        </>
      }
    >
      <div className="guide-step" aria-live="polite">
        <div className="guide-picture" aria-hidden="true">
          {PICTURES[step]}
        </div>
        <h3>{t(`campaignGuide.${n}.title` as MessageKey)}</h3>
        <p>{t(`campaignGuide.${n}.text` as MessageKey)}</p>
      </div>
    </Modal>
  );
}
